import React, { useState, useEffect, useRef } from 'react';
import type { Address, Store } from '../../types/index';
import { Search, AlertCircle, Home, Navigation, Loader2, X } from 'lucide-react';
// import { formatPrice } from '../../utils/utilityFunctions';
import { DEFAULT_COUNTRY_CODE, PhoneInput, validatePhoneNumber } from '../common/PhoneInput';
import { SavedLocationNotice } from './SavedLocationNotice';
import LocationWarningModal from '../common/LocationWarningModal';
import { formatCoord } from '../../utils/utilityFunctions';
import CountrySelect from '../common/CountrySelect';
import StoreSelect from '../vendor/StoreSelect';
import { storesAPI } from '../../api/stores';
import ConfirmationModal from '../common/ConfirmationModal';

interface AddressFormProps {
  initialData?: Partial<Address>;
  cities: Array<{ id: number; name: string; delivery_fee: number }>;
  onSubmit: (data: any) => Promise<void> | void;
  onCancel: () => void;
  onSkipHouseNumber?: () => void;
  onVerifyHomeAddress?: () => void;
  isHomeAddress?: boolean;
  isVerifiedHome?: boolean;
  onSaveCoordinates?: (addressId: number, coordinates: { latitude: number; longitude: number }) => Promise<void>;
  mode?: 'customer' | 'vendor' | 'store';
  showEmailField?: boolean;
  defaultStoreId?: number | null;
  storeOptions?: Store[];
}

export const AddressForm: React.FC<AddressFormProps> = ({
  initialData,
  cities = [],
  onSubmit,
  onCancel,
  onSkipHouseNumber,
  onVerifyHomeAddress,
  isHomeAddress = false,
  isVerifiedHome = false,
  mode = 'customer',
  showEmailField,
  defaultStoreId,
  storeOptions,
}) => {
  const [formData, setFormData] = useState({
    store: defaultStoreId ? defaultStoreId : (initialData?.store || ''),
    recipient_name: initialData?.recipient_name || '',
    label: initialData?.label,
    house_number: initialData?.house_number || '',
    address_line_1: initialData?.address_line_1 || '',
    address_line_2: initialData?.address_line_2 || '',
    contact_number_1: initialData?.contact_number_1 || DEFAULT_COUNTRY_CODE,
    contact_number_2: initialData?.contact_number_2 || '',
    email: initialData?.email || '',
    city: initialData?.city || '',
    country: initialData?.country || 'TT',
    delivery_note: initialData?.delivery_note || '',
    is_default: initialData?.is_default || false,
    location_name: initialData?.location_name || '',
  });

  const [searchCity, setSearchCity] = useState<string | null>(null);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [skipHouseNumber, setSkipHouseNumber] = useState(false);
  const [houseNumberAttempts, setHouseNumberAttempts] = useState(0);
  const [showHouseNumberPrompt, setShowHouseNumberPrompt] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [capturedLocation, setCapturedLocation] = useState<{ lat?: number; lng?: number }>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSavedLocationNotice, setShowSavedLocationNotice] = useState(true);
  const houseNumberRef = useRef<HTMLDivElement>(null);
  const [showLocationWarning, setShowLocationWarning] = useState(false);
  const prevLabelRef = useRef(formData.label);
  const [stores, setStores] = useState<Store[]>([]);
  const [showGeneralLocationWarning, setShowGeneralLocationWarning] = useState(false);

  // Filter cities based on search
  const filteredCities = (cities || [])
    .filter(city => {
      // Guard against undefined city
      if (!city || !city.name) return false;

      // If no search term, include all
      if (!searchCity?.trim()) return true;

      // Filter by search term
      return city.name.toLowerCase().includes(searchCity.toLowerCase());
    })
    .slice(0, 10); // Limit to 10 results for mobile

  const selectedCity = cities.find(c => c.id === formData.city);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.store && mode === 'vendor') {
      newErrors.store = 'Please select a store';
    }

    if (!formData.label) {
      newErrors.label = 'Please choose a label';
    }

    if (!formData.recipient_name.trim()) {
      mode === 'customer' && (newErrors.recipient_name = 'Recipient name is required');
      mode === 'vendor' && (newErrors.recipient_name = 'Pickup contact name is required');
      mode === 'store' && (newErrors.recipient_name = 'Contact name is required');
    }

    if (!formData.contact_number_1.trim()) {
      newErrors.contact_number_1 = 'Primary contact number is required';
    }

    if (showEmailField && formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validations
    const phone1Error = validatePhoneNumber(formData.contact_number_1, true);
    if (phone1Error) newErrors.contact_number_1 = phone1Error;

    const phone2Error = validatePhoneNumber(formData.contact_number_2, false);
    if (phone2Error) newErrors.contact_number_2 = phone2Error;

    if (!formData.address_line_1.trim()) {
      newErrors.address_line_1 = 'Main road is required';
    }

    if (!formData.city) {
      newErrors.city = 'Please select a city';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const scrollToHouseNumber = () => {
    houseNumberRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({}); // Clear previous errors

    if (!validateForm()) {
      // Scroll to first error
      const firstError = Object.keys(errors)[0];
      if (firstError) {
        document.querySelector(`[name="${firstError}"]`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
      return;
    }

    // Check house number requirement
    if (!formData.house_number.trim() && houseNumberAttempts === 0) {
      setShowHouseNumberPrompt(true);
      setHouseNumberAttempts(1);
      scrollToHouseNumber();
      return;
    }

    // Format coordinates before validation
    let formattedLat: number | undefined;
    let formattedLng: number | undefined;
    if (capturedLocation.lat !== undefined && capturedLocation.lng !== undefined) {
      formattedLat = formatCoord(capturedLocation.lat);
      formattedLng = formatCoord(capturedLocation.lng);
    }

    if (!formData.house_number.trim() && houseNumberAttempts === 1) {
      // Second attempt - allow skipping
      await onSubmit({
        ...formData, house_number: '',
        city_details: selectedCity || null,
        latitude: formattedLat,
        longitude: formattedLng
      });
      setSkipHouseNumber(true);
      onSkipHouseNumber?.();
      return;
    }

    await onSubmit({
      ...formData,
      city_details: selectedCity || null,
      latitude: formattedLat,
      longitude: formattedLng
    });
  };

  useEffect(() => {
    if (storeOptions) {
      setStores(storeOptions);
      return;
    }
    const fetchStores = async () => {
      try {
        const data = await storesAPI.getStores();
        setStores(data);
      } catch (error) {
        console.error('Failed to fetch stores:', error);
      }
    };
    if (mode === 'vendor') fetchStores();
    if (mode === 'store') setFormData({ ...formData, label: 'Public Address' })
  }, [storeOptions]);

  useEffect(() => {
    // When a city is selected, show its name in the search field
    if (selectedCity && searchCity !== selectedCity.name) {
      setSearchCity(selectedCity.name);
    }
  }, [selectedCity]);

  useEffect(() => {
    // If label changed to 'Home' and it wasn't already 'Home'
    if (formData.label === 'Home' && prevLabelRef.current !== 'Home') {
      // Clear location data when switching to Home to force recapture
      setCapturedLocation({});
    }

    // Update the ref for next render
    prevLabelRef.current = formData.label;
  }, [formData.label]);

  const handleCaptureLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported');
      return;
    }

    if (formData.label === 'Home') {
      setShowLocationWarning(true);
    } else {
      setShowGeneralLocationWarning(true);
    }
  };

  const confirmGeneralLocation = () => {
    setShowGeneralLocationWarning(false);
    captureLocation();
  };

  const buildGeneralLocationMessage = () => {
    const addressParts = [formData.house_number, formData.address_line_1].filter(Boolean);
    const locationLine = addressParts.length > 0 ? addressParts.join(' ') : null;

    return (
      <div className="space-y-3">
        <p className="text-sm text-gray-700">
          You're about to save your current location{locationLine ? ' for:' : '.'}
        </p>
        {locationLine && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-blue-800">Address:</p>
            <p className="text-sm text-blue-700">{locationLine}</p>
          </div>
        )}
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <Navigation className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                Are you at this location?
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Your GPS coordinates will be saved for accurate pickup and delivery.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const captureLocation = () => {
    setIsGettingLocation(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setCapturedLocation({ lat: latitude, lng: longitude });
        setIsGettingLocation(false);
        // Optionally update form with coordinates or show success message
      },
      (error) => {
        setIsGettingLocation(false);
        switch (error.code) {
          case 1: setLocationError('Permission denied'); break;
          case 2: setLocationError('Location unavailable'); break;
          case 3: setLocationError('Request timed out'); break;
          default: setLocationError('Failed to get location');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );

  }

  useEffect(() => {
    if (showCityDropdown) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showCityDropdown]);

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-md w-full p-4 my-auto">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {initialData ? 'Edit Address' : 'Add New Address'}
        </h3>

        {/* Home Address Verification Notice */}
        {isHomeAddress && !isVerifiedHome && onVerifyHomeAddress && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <Home className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-blue-800 font-medium">Home Address Verification Required</p>
                <p className="text-xs text-blue-600 mt-1">
                  Verify your home address to ship to other locations.
                </p>
                <button
                  type="button"
                  onClick={onVerifyHomeAddress}
                  className="mt-2 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                >
                  Verify Home Address
                </button>
              </div>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          {mode === 'vendor' && (
            <div className="mb-4">
              <StoreSelect
                name="store"
                value={formData.store}
                onChange={(value) => {
                  setFormData({ ...formData, store: value })
                  if (errors.store) {
                    setErrors(prev => ({ ...prev, store: '' }));
                  }
                }}
                label="Store"
                placeholder="Select a store..."
                stores={stores}
                error={errors.store}
                required
              />
            </div>
          )}
          {/* Recipient Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {mode === 'customer' ? 'Recipient Name' : mode === 'vendor' ? 'Pickup Contact Name' : 'Contact Name'} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.recipient_name}
              name="recipient_name"
              onChange={(e) => {
                // Clear error when user starts typing
                if (errors.recipient_name) {
                  setErrors(prev => ({ ...prev, recipient_name: '' }));
                }
                setFormData({ ...formData, recipient_name: e.target.value })
              }
              }
              className={`w-full border rounded-lg shadow-sm p-3 text-sm transition-colors ${errors.recipient_name
                ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                }`}
              placeholder={mode === 'customer' ? "Full name of person receiving delivery" : mode === 'vendor' ? "Full name of pickup contact" : "Full name of contact"}
            />
            {errors.recipient_name && (
              <div className="mt-1 flex items-center text-sm text-red-600">
                <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                {errors.recipient_name}
              </div>
            )}
          </div>
          <div className="space-y-4">
            {/* Label */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Label <span className="text-red-500">*</span></label>
              <div className={`grid gap-2 ${mode === 'vendor' ? 'grid-cols-4' : mode === 'customer' ? 'grid-cols-3' : 'grid-cols-1'}`}>
                {(mode === 'customer'
                  ? ['Home', 'Work', 'Other'] : mode === 'vendor' ? ['Residence', 'Office', 'Store', 'Other'] : ['Public Address']).map((label) => (
                    <button
                      key={label}
                      type="button"
                      name="label"
                      onClick={() => {
                        setFormData({ ...formData, label })
                        if (errors.label) {
                          setErrors(prev => ({ ...prev, label: '' }));
                        }
                      }}
                      className={`py-2 px-3 text-sm font-medium rounded-md transition-colors ${formData.label === label
                        ? errors.label
                          ? 'bg-red-100 border border-red-300 text-red-700' // Error + selected
                          : 'bg-primary-600 text-white' // Normal selected
                        : errors.label
                          ? 'bg-red-50 border border-red-200 text-red-600 hover:bg-red-100' // Error + not selected
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200' // Normal not selected
                        } ${errors.label ? 'border' : ''}`}
                    >
                      {label}
                    </button>
                  ))}
              </div>
              {errors.label && (
                <div className="mt-1 flex items-center text-sm text-red-600">
                  <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                  {errors.label}
                </div>
              )}
            </div>

            {/* Contact Numbers */}
            <div className="mb-4">
              <PhoneInput
                value={formData.contact_number_1}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, contact_number_1: value || '' }));
                  if (errors.contact_number_1) {
                    setErrors(prev => ({ ...prev, contact_number_1: '' }));
                  }
                }}
                label="Primary Contact Number"
                error={errors.contact_number_1}
                name="contact_number_1"
              />

              <PhoneInput
                value={formData.contact_number_2}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, contact_number_2: value || '' }));
                  if (errors.contact_number_2) {
                    setErrors(prev => ({ ...prev, contact_number_2: '' }));
                  }
                }}
                label="Secondary Contact Number (Recommended)"
                error={errors.contact_number_2}
                name="contact_number_2"
              />
            </div>
            {showEmailField && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Email <span className="text-xs text-gray-500">(optional)</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email || ''}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, email: e.target.value }));
                    if (errors.email) {
                      setErrors(prev => ({ ...prev, email: '' }));
                    }
                  }}
                  className={`w-full border rounded-lg shadow-sm p-3 text-sm focus:border-primary-500 focus:ring-primary-500 ${errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  placeholder="contact@example.com"
                />
                {errors.email && (
                  <div className="mt-1 flex items-center text-sm text-red-600">
                    <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                    {errors.email}
                  </div>
                )}
                {/* <p className="mt-1 text-xs text-gray-500">
                  This may be displayed publicly on your storefront.
                </p> */}
              </div>
            )}
            {/* Conditional Field for Work/Other Label */}
            {(formData.label === 'Work' || formData.label === 'Other') && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {formData.label === 'Work' ? (
                    <>Business / Company Name <span className="text-xs text-gray-500">(optional)</span></>
                  ) : (
                    <>Recipient / Place Name <span className="text-xs text-gray-500">(e.g., Grandma's house, Friend's apartment)</span></>
                  )}
                </label>
                <input
                  type="text"
                  value={formData.location_name || ''}
                  onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg shadow-sm p-3 text-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder={formData.label === 'Work'
                    ? "e.g., Acme Corporation, Smith & Sons"
                    : "e.g., Maria's Apartment, David's House"}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.label === 'Work'
                    ? "The name of the business at this location"
                    : "Help identify this address (useful if it's not your own)"}
                </p>
              </div>
            )}
            {/* House Number & Street Address - Side by Side */}
            <div ref={houseNumberRef} className="grid grid-cols-3 gap-3">
              {/* House Number - Small Column */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {mode === 'customer' ? 'House' : 'Lot'} or Lp #
                  {skipHouseNumber && (
                    <span className="ml-1 text-xs text-gray-500 italic">(none)</span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.house_number}
                    onChange={(e) => setFormData({ ...formData, house_number: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg shadow-sm p-3 text-sm"
                    placeholder="12"
                    disabled={skipHouseNumber}
                    inputMode="numeric"
                    pattern="[0-9.]*"
                  />
                </div>
                {skipHouseNumber ? (
                  <button
                    type="button"
                    onClick={() => setSkipHouseNumber(false)}
                    className="mt-1 text-xs text-primary-600 hover:text-primary-800"
                  >
                    Add number
                  </button>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">Just the number</p>
                )}
              </div>

              {/* Street Address - Larger Column */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Street / Road / Avenue <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.address_line_1}
                  name="address_line_1"
                  onChange={(e) => {
                    setFormData({ ...formData, address_line_1: e.target.value });
                    if (errors.address_line_1) {
                      setErrors(prev => ({ ...prev, address_line_1: '' }));
                    }
                  }}
                  className={`w-full border rounded-lg shadow-sm p-3 text-sm transition-colors ${errors.address_line_1
                    ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                    }`}
                  placeholder="e.g., Main Street, John Doe Drive"
                />
                <p className="mt-1 text-xs text-gray-500">Street your {mode === 'customer' && formData.label === 'Home' ? 'house' : 'building'} is on</p>
                {errors.address_line_1 && (
                  <div className="mt-1 flex items-center text-sm text-red-600">
                    <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                    {errors.address_line_1}
                  </div>
                )}
              </div>
            </div>

            {/* House Number Prompt */}
            {showHouseNumberPrompt && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-yellow-800 font-medium">Add {mode === 'customer' ? 'House' : 'Lot'} Number</p>
                    <p className="text-xs text-yellow-600 mt-1">
                      Please add just the number (e.g., 12, 45, 3.5). Put the road name in the field below.
                    </p>
                    <div className="mt-2 flex space-x-2">
                      <button
                        type="button"
                        onClick={() => setShowHouseNumberPrompt(false)}
                        className="text-xs bg-yellow-600 text-white px-3 py-1 rounded hover:bg-yellow-700"
                      >
                        Add Number
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowHouseNumberPrompt(false);
                          setHouseNumberAttempts(1);
                        }}
                        className="text-xs border border-yellow-300 text-yellow-700 px-3 py-1 rounded hover:bg-yellow-100"
                      >
                        Skip
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Address Line 2 - Side Street/Area */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Main Road (optional)
              </label>
              <input
                type="text"
                value={formData.address_line_2}
                onChange={(e) => setFormData({ ...formData, address_line_2: e.target.value })}
                className="w-full border border-gray-300 rounded-lg shadow-sm p-3 text-sm"
                placeholder="e.g., off Eastern Main Road, Southern Main Rd, Almond Drive"
              />
              <p className="mt-1 text-xs text-gray-500">
                Helpful for finding your location
              </p>
            </div>
            {/* City Search Dropdown */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">City <span className="text-red-500">*</span></label>
              <div className="relative flex items-center">
                <Search className="absolute left-3 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={searchCity ?? ''}
                  name="city"
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchCity(value);
                    setShowCityDropdown(true);

                    if (value === '') {
                      setFormData({ ...formData, city: '' });
                    }
                  }}
                  onFocus={() => setShowCityDropdown(true)}
                  onBlur={() => {
                    // Delay closing to allow button clicks
                    setTimeout(() => setShowCityDropdown(false), 200);
                  }}
                  placeholder="Search for your city..."
                  className={`w-full border rounded-lg shadow-sm pl-10 pr-10 py-3 text-sm transition-colors ${errors.city
                    ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                    }`}
                  style={{ lineHeight: 'normal' }}
                />
                {searchCity && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchCity(null);
                      setFormData({ ...formData, city: cities[0]?.id || '' });
                      setShowCityDropdown(true);
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 px-1"
                  >
                    ✕
                  </button>
                )}
              </div>
              {errors.city && !showCityDropdown && (
                <div className="mt-1 flex items-center text-sm text-red-600">
                  <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                  {errors.city}
                </div>
              )}
              {/* City Dropdown */}
              {showCityDropdown && filteredCities.length > 0 && (
                <>
                  {/* Overlay */}
                  <div
                    className="fixed inset-0 z-40 bg-black bg-opacity-10"
                    onClick={() => setShowCityDropdown(false)}
                  />

                  {/* Dropdown positioned ABSOLUTE to input container - no gap */}
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto overscroll-contain">
                    {filteredCities.map(city => (
                      <div
                        key={city.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData({ ...formData, city: city.id });
                          setSearchCity(city.name);
                          setShowCityDropdown(false);
                          // Clear error when user interacts
                          if (errors.city) {
                            setErrors(prev => ({ ...prev, city: '' }));
                          }
                        }}
                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 active:bg-gray-100 border-b border-gray-100 last:border-b-0 cursor-pointer select-none ${city.id === formData.city ? 'bg-primary-50 border-l-4 border-l-primary-500' : ''
                          }`}
                      >
                        <div className="font-medium text-gray-900">{city.name}</div>
                        {/* <div className="text-xs text-gray-500">
                              Delivery: {formatPrice(city.delivery_fee)}
                            </div>
                        */}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <CountrySelect
              value={formData.country || 'TT'}
              onChange={(value) => {
                setFormData({ ...formData, country: value });
                if (errors.country) {
                  const newErrors = { ...errors };
                  delete newErrors.country;
                  setErrors(newErrors);
                }
              }}
              error={errors.country}
              label="Country"
              className='mt-2'
              required={true}
            />
            {/* Delivery Instructions / Pickup Instructions */}
            {mode !== 'store' &&
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {mode === 'customer' ? 'Delivery Instructions' : 'Pickup Instructions'} (optional)
                </label>
                <textarea
                  value={formData.delivery_note}
                  onChange={(e) => setFormData({ ...formData, delivery_note: e.target.value })}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg shadow-sm p-3 text-sm"
                  placeholder="e.g., Red gate, call on arrival, security code 1234"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Specific instructions for the delivery person
                </p>
              </div>
            }

            {/* Default Address Toggle */}
            {mode !== 'store' &&
              <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={formData.is_default}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  className="h-5 w-5 text-primary-600 border-gray-300 rounded"
                />
                <label htmlFor="is_default" className="ml-3 text-sm text-gray-700">
                  {mode === 'customer' ? 'Set as default shipping address' : 'Set as default pickup address'}
                </label>
              </div>
            }
          </div>
          {capturedLocation.lat && capturedLocation.lng && (
            <div className="mt-4 mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Navigation className="h-5 w-5 text-green-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Location Captured</p>
                    <p className="text-xs text-green-600 mt-1">
                      Coordinates saved: {capturedLocation.lat.toFixed(6)}, {capturedLocation.lng.toFixed(6)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setCapturedLocation({ lat: undefined, lng: undefined })}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  title="Clear location"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          )}
          {/* Add location capture button*/}
          <div className="mt-4">
            {/* Saved Location Notice */}
            {initialData?.coordinates_saved_at && showSavedLocationNotice && (
              <SavedLocationNotice
                savedAt={initialData.coordinates_saved_at!}
                onUpdateLocation={() => {
                  // Trigger location capture
                  handleCaptureLocation();
                  setShowSavedLocationNotice(false);
                }}
                isEditing={!!initialData}
              />
            )}
            {!initialData?.coordinates_saved_at && (
              <>
                {/* Location Capture Warning - Only for Home addresses */}
                {formData.label === 'Home' && (
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <p className="font-medium mb-1">⚠️ Important: Location Verification Required</p>
                        <p className="mb-2">
                          Home addresses must be verified using your current location. Please ensure you are at your
                          actual home address when capturing location.
                        </p>
                        <p className="text-amber-700">
                          <strong>Do not submit:</strong>
                        </p>
                        <ul className="list-disc ml-4 mt-1 space-y-1 text-amber-700">
                          <li>A false or incorrect location</li>
                          <li>Your work address as your home</li>
                          <li>Someone else's address</li>
                        </ul>
                        <p className="mt-2 text-amber-700 font-medium">
                          ❌ Submitting false information will result in rejection and may lead to your account being flagged or banned for suspicious activity.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleCaptureLocation}
                  disabled={isGettingLocation}
                  className="w-full flex items-center justify-center gap-2 border border-primary-300 text-primary-700 hover:bg-primary-50 rounded-lg py-2 px-4 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGettingLocation ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Getting Location...
                    </>
                  ) : (
                    <>
                      <Navigation className="h-4 w-4" />
                      Capture Current Location
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Use this if you're currently at this address to save GPS coordinates for better {mode === 'customer' ? 'delivery' : mode === 'vendor' ? 'pickup' : ''} accuracy.
                </p>
              </>
            )}
            {locationError && (
              <p className="text-xs text-red-600 mt-2">{locationError}</p>
            )}
          </div>
          {/* Action Buttons */}
          <div className="mt-6 pt-4 border-t border-gray-200 flex flex-col space-y-3">
            <button
              type="submit"
              className="w-full bg-primary-600 border border-transparent rounded-lg py-3 px-4 text-sm font-medium text-white hover:bg-primary-700 active:bg-primary-800 transition-colors"
            >
              {initialData ? 'Update Address' : 'Save Address'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="w-full border border-gray-300 rounded-lg py-3 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* Close dropdown when clicking outside */}
      {showCityDropdown && (
        <div
          className="fixed inset-0 z-40"
          onTouchStart={(e) => {
            e.preventDefault();
            setShowCityDropdown(false);
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            setShowCityDropdown(false);
          }}
        />
      )}
      {/* Warning modal */}
      {showLocationWarning && (
        <LocationWarningModal
          isOpen={showLocationWarning}
          onClose={() => setShowLocationWarning(false)}
          onConfirm={captureLocation}
        />)}

      {/* General Location Capture Confirmation Modal*/}
      {showGeneralLocationWarning &&
        <ConfirmationModal
          isOpen={showGeneralLocationWarning}
          onClose={() => setShowGeneralLocationWarning(false)}
          onConfirm={confirmGeneralLocation}
          title="Confirm Your Location"
          message={buildGeneralLocationMessage()}
          confirmText="Yes, I'm Here"
          cancelText="Cancel"
          type="info"
          isProcessing={isGettingLocation}
        />
      }
    </div>
  );
};

export default AddressForm;